import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { queryNodeUrval } from '../../../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/queryNodeUrval';
import { convertUrval } from '../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import { ModelContext } from '../../../../../Model/ModelContext/ModelContext';
import { IUIUrval, RutinUrvalType } from '../../../../../Types/UrvalTypes';
import Logger from '../../../../../Utils/Logger';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import { equals } from 'ramda';
import { removeIDsFromIUIUrval } from '../../../../../Components/Urval/Utils/urvalUtils';
import { useIsSmallBreakpoint } from '../../../../../Components/BreakpointContext/BreakpointContextUtils';
import { VisningsTypContext } from '../BusinessCollectionComponents/VisningsTypContext/VisningsTypContext';

interface IProps {
  collection: IClientBusinessModel;
  rutinUrvalList?: RutinUrvalType[] | null;
  urval: IUIUrval | null;
  urvalID: string | undefined;
}

const SelectionNameLabel: React.FC<React.PropsWithChildren<IProps>> = props => {
  const apolloClient = useApolloClient();
  const modelContext = useContext(ModelContext);
  const [baseUrval, setBaseUrval] = useState<IUIUrval | null>(null);
  const isSmallScreen = useIsSmallBreakpoint();
  const visningsTypContext = useContext(VisningsTypContext);

  const hasUrval = useMemo(() => Boolean(props.urval), [props.urval]);

  const savedSelection = useMemo(
    () =>
      props.rutinUrvalList?.find(urval => {
        return urval.id === props.urvalID;
      }),
    [props.rutinUrvalList, props.urvalID]
  );

  const selectionIsEqual = useMemo(() => {
    return equals(
      removeIDsFromIUIUrval(baseUrval),
      removeIDsFromIUIUrval(props.urval)
    );
  }, [baseUrval, props.urval]);

  const selectionName = useMemo(
    () =>
      hasUrval
        ? selectionIsEqual
          ? savedSelection?.namn ??
            (props.urvalID
              ? 'Urvalets namn kunde inte hämtas'
              : 'Inget urval angivet')
          : 'Eget Urval'
        : savedSelection?.namn ??
          (props.urvalID
            ? 'Urvalets namn kunde inte hämtas'
            : 'Inget urval angivet'),
    [hasUrval, props.urvalID, savedSelection?.namn, selectionIsEqual]
  );

  useEffect(() => {
    if (props.urvalID) {
      if (baseUrval?.savedUrvalProps?.id != props.urvalID && hasUrval) {
        queryNodeUrval(apolloClient, props.urvalID)
          .then(options => {
            setBaseUrval(
              convertUrval(
                options,
                props.collection.CollectionName,
                modelContext.avenyCollectionMap
              )
            );
          })
          .catch(error => {
            Logger.error(error);
          });
      }
    }
  }, [
    apolloClient,
    baseUrval,
    hasUrval,
    modelContext.avenyCollectionMap,
    props.collection.CollectionName,
    props.urvalID,
  ]);

  return (
    <EDBox
      marginX={
        visningsTypContext.visningsTyp === 'kort' || isSmallScreen ? 0 : 1
      }
      sx={{ display: 'flex', flexFlow: 'row nowrap', alignItems: 'flex-end' }}
    >
      <EDIcon iconType="filter_list" />
      <EDTypography>
        <EDTypography bold component="span">
          Urval:
        </EDTypography>{' '}
        {selectionName}
      </EDTypography>
    </EDBox>
  );
};

export default SelectionNameLabel;
